import { useRef, memo, useCallback } from "react";
import { useShallow } from "zustand/shallow";
import SelectRouteSearch from "./components/selectRouteSearch";
import { useMapRouteStore } from "@/stores/mapRouteStore";

const RouteForm = memo(function RouteForm() {
  const { route, setRoute } = useMapRouteStore(
    useShallow((s) => ({
      route: s.route,
      setRoute: s.setRoute,
    })),
  );
  const startFieldRef = useRef<HTMLDivElement>(null);
  const endFieldRef = useRef<HTMLDivElement>(null);

  const setStartValue = useCallback(
    (value: string) => {
      setRoute((r) => ({ ...r, start: value }));
    },
    [setRoute]
  );

  const clearStartValue = useCallback(() => {
    setRoute((r) => ({ ...r, start: "" }));
  }, [setRoute]);

  const setEndValue = useCallback(
    (value: string) => {
      setRoute((r) => ({ ...r, end: value }));
    },
    [setRoute]
  );

  const clearEndValue = useCallback(() => {
    setRoute((r) => ({ ...r, end: "" }));
  }, [setRoute]);

  const handleSwap = useCallback(() => {
    setRoute((r) => ({ start: r.end, end: r.start }));
  }, [setRoute]);

  return (
    <div className="px-4 pt-4 pb-4 font-medium">
      <div className="flex items-stretch gap-2">
        {/* Route fields */}
        <div className="flex-1 flex flex-col gap-2">
          {/* Start point */}
          <div
            ref={startFieldRef}
            className="flex items-center bg-white border border-gray-200 rounded-[10px] px-[14px] py-3 hover:border-rudn-blue transition-colors"
          >
            <SelectRouteSearch
              setValue={setStartValue}
              value={route.start}
              clearValue={clearStartValue}
              containerRef={startFieldRef}
              dotColor="#1a5fb4"
            />
          </div>

          {/* End point */}
          <div
            ref={endFieldRef}
            className="flex items-center bg-white border border-gray-200 rounded-[10px] px-[14px] py-3 hover:border-rudn-blue transition-colors"
          >
            <SelectRouteSearch
              setValue={setEndValue}
              value={route.end}
              clearValue={clearEndValue}
              containerRef={endFieldRef}
              dotColor="#C4A962"
            />
          </div>
        </div>

        {/* Swap button */}
        <div className="flex items-center pl-2">
          <button
            className={`w-9 h-9 border border-gray-200 rounded-lg flex items-center justify-center transition-colors ${
              route.end && route.start
                ? "hover:border-rudn-blue hover:text-rudn-blue hover:bg-rudn-blue/5 cursor-pointer"
                : "opacity-40 cursor-default"
            }`}
            disabled={!route.end || !route.start}
            onClick={handleSwap}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              className="w-[18px] h-[18px] text-gray-500"
            >
              <path d="M7 16V4m0 0L3 8m4-4l4 4M17 8v12m0 0l4-4m-4 4l-4-4" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
});

export default RouteForm;
